import { Card, Title, Icon } from "@introist/react-foundation/v2";
import styled from "styled-components";
import { WellbeingAggregateStats } from "../hooks/useWellbeingData";

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--spacing-medium);
`;

const StatCard = styled(Card)<{ $variant?: "default" | "danger" | "warning" | "success" }>`
  padding: var(--spacing-large);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-small);
  border-left: 4px solid ${({ $variant }) => {
    switch ($variant) {
      case "danger":
        return "var(--palette-danger-default)";
      case "warning":
        return "var(--palette-warning-default)";
      case "success":
        return "var(--palette-success-default)";
      default:
        return "var(--palette-primary-default)";
    }
  }};
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
  color: var(--palette-text-secondary);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  color: var(--palette-text-primary);
`;

const StatSubtext = styled.div`
  font-size: 0.875rem;
  color: var(--palette-text-secondary);
  display: flex;
  align-items: center;
  gap: var(--spacing-xSmall);
`;

const TrendIcon = styled.span<{ $direction: "up" | "down" | "stable" }>`
  color: ${({ $direction }) => {
    switch ($direction) {
      case "up":
        return "var(--palette-success-default)";
      case "down":
        return "var(--palette-danger-default)";
      default:
        return "var(--palette-text-secondary)";
    }
  }};
`;

type WellbeingOverviewCardsProps = {
  stats: WellbeingAggregateStats;
  activeInterventions?: number;
  showTeamWellbeing?: boolean;
};

export const WellbeingOverviewCards = ({ stats, activeInterventions = 0, showTeamWellbeing = true }: WellbeingOverviewCardsProps) => {
  const getTrendLabel = (direction: "up" | "down" | "stable") => {
    switch (direction) {
      case "up":
        return "Improving";
      case "down":
        return "Declining";
      default:
        return "Stable";
    }
  };

  const getTrendIcon = (direction: "up" | "down" | "stable") => {
    switch (direction) {
      case "up":
        return "↗";
      case "down":
        return "↘";
      default:
        return "→";
    }
  };

  return (
    <CardsGrid>
      <StatCard $variant="warning">
        <StatHeader>
          <Icon name="warning" style={{ fontSize: 20 }} />
          <Title>Employees at Risk</Title>
        </StatHeader>
        <StatValue>{stats.totalAtRisk}</StatValue>
        <StatSubtext>
          of {stats.totalEmployees} total employees
        </StatSubtext>
      </StatCard>

      <StatCard $variant="danger">
        <StatHeader>
          <Icon name="exclamationCircle" style={{ fontSize: 20 }} />
          <Title>High Severity Cases</Title>
        </StatHeader>
        <StatValue>{stats.highSeverityCount}</StatValue>
        <StatSubtext>
          Requiring immediate attention
        </StatSubtext>
      </StatCard>

      <StatCard $variant={activeInterventions > 0 ? "success" : "default"}>
        <StatHeader>
          <Icon name="workflows" style={{ fontSize: 20 }} />
          <Title>Active Interventions</Title>
        </StatHeader>
        <StatValue>{activeInterventions}</StatValue>
        <StatSubtext>
          Workflows currently in progress
        </StatSubtext>
      </StatCard>

      {showTeamWellbeing && (
        <StatCard $variant={stats.trendDirection === "up" ? "success" : stats.trendDirection === "down" ? "danger" : "default"}>
          <StatHeader>
            <Icon name="persons" style={{ fontSize: 20 }} />
            <Title>Team Wellbeing</Title>
          </StatHeader>
          <StatValue>{stats.averageWellbeingScore}%</StatValue>
          <StatSubtext>
            <TrendIcon $direction={stats.trendDirection}>
              {getTrendIcon(stats.trendDirection)}
            </TrendIcon>
            {getTrendLabel(stats.trendDirection)} trend
          </StatSubtext>
        </StatCard>
      )}
    </CardsGrid>
  );
};
